import React, { useState, useEffect } from "react";
import Layout from "../Layout";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import UserLogs from "./UserLogs";
import { format } from "date-fns";
import SummaryCard from "./SummaryCard";
import { debounce, set } from "lodash";
import axios from "axios";
import ActiveModuleEditor from "./ActiveModuleEditor";

const User = ({ userId, onClose, onCloseTitle }) => {
  const [user, setUser] = useState({});
  const [conversation, setConversation] = useState(user.log);
  const [headlineSummary, setHeadlineSummary] = useState(""); // New state for headline summary
  const name = user?.core_memory && user?.core_memory?.name;
  const [humanCoachContext, setHumanCoachContext] = useState("");
  const [loading, setLoading] = useState(true);

  // Add a debounced function to handle updates to the server
  const axiosWithAuth = () => {
    const token = localStorage.getItem("coachJwtToken");
    return axios.create({
      baseURL: "https://api.1440.ai/",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await axiosWithAuth().get(
          `/api/coach/users/${userId}`
        );
        setUser(response.data);
        setConversation(response.data.log);
        if (response.data.agents && response.data.agents.human_coach_context) {
          setHumanCoachContext(response.data.agents.human_coach_context);
        }
        if (response.data.headline_summary) {
          setHeadlineSummary(response.data.headline_summary); // Set headline summary from the user data
        } else {
          setHeadlineSummary(
            "Currently, there is no user overview available for this client."
          ); // Set headline summary from the user data
        }
      } catch (error) {
        console.error("Failed to fetch user details:", error);
        if (error.response && error.response.status === 401) {
          // Handle unauthorized access (e.g., redirect to login)
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();

    const updateLogs = async () => {
      try {
        const response = await axiosWithAuth().get(
          `/api/coach/users/${userId}`
        );
        setConversation(response.data.log);
      } catch (error) {
        console.error("Failed to update logs:", error);
      }
    };

    const intervalId = setInterval(updateLogs, 5000);

    return () => clearInterval(intervalId);
  }, [userId]);

  const debounceUpdateUserContext = debounce(async (userId, context) => {
    try {
      const response = await axiosWithAuth().put(
        `/users/${userId}/context/${encodeURIComponent(context)}`
      );
    } catch (error) {
      console.error("Failed to update user context:", error);
    }
  }, 1500);

  const handleHumanCoachContextChange = (value) => {
    setHumanCoachContext(value);
    if (user.user_id) {
      debounceUpdateUserContext(user.user_id, value);
    }
  };

  const markSummaryAsRead = async (summaryId) => {
    const index = user.summaries.length - 1 - summaryId;
    setUser((prevUser) => {
      const updatedUser = { ...prevUser };
      updatedUser.summaries[index].read = true;
      return updatedUser;
    });
    try {
      await axiosWithAuth().put(`/users/${userId}/summary/${index}`);
    } catch (error) {
      console.error("Failed to mark summary as read:", error);
    }
  };

  const update_conversation = (message) => {
    setConversation([...conversation, message]);
  };

  // --- Active Module Logic ---
  const debounceUpdateActiveModule = debounce(
    async (userId, protocolPayload) => {
      try {
        // Aligning with the existing updateUserProtocol pattern
        // and removing trailing slash from the path based on 307 redirect
        await axiosWithAuth().post(
          "https://api.1440.ai/user_protocol/update", // No trailing slash
          {
            protocol_data: protocolPayload, // This is user.protocols[0] with updated active_module
            user_id: userId,
          }
        );
      } catch (error) {
        console.error("Failed to update active module:", error);
        // Optionally, revert local state or show error to user
      }
    },
    1500
  );

  const handleActiveModuleUpdate = (updatedActiveModule) => {
    setUser((prevUser) => {
      const newProtocols = prevUser.protocols ? [...prevUser.protocols] : [];
      if (newProtocols.length > 0) {
        newProtocols[0] = {
          ...newProtocols[0],
          active_module: updatedActiveModule,
        };
      } else {
        // If no protocols array or it's empty, create one with the active module
        // This case might need more robust handling depending on app logic
        newProtocols.push({
          protocol_id: "temp-" + Date.now(),
          active_module: updatedActiveModule,
        });
      }
      const updatedUser = { ...prevUser, protocols: newProtocols };
      if (updatedUser.protocols[0]) {
        debounceUpdateActiveModule(prevUser.user_id, updatedUser.protocols[0]);
      }
      return updatedUser;
    });
  };

  const handleRemoveActiveModule = async () => {
    setUser((prevUser) => {
      const newProtocols = prevUser.protocols ? [...prevUser.protocols] : [];
      let protocolPayload = null;
      if (newProtocols.length > 0) {
        newProtocols[0] = { ...newProtocols[0], active_module: null };
        protocolPayload = newProtocols[0];
      }
      // If protocolPayload is still null, it means there was no protocol to update.
      // The backend call might not be needed or should handle this.
      // For now, we'll call it if a payload was prepared.
      if (protocolPayload && prevUser.user_id) {
        debounceUpdateActiveModule(prevUser.user_id, protocolPayload);
      }
      return { ...prevUser, protocols: newProtocols };
    });
  };
  // --- End Active Module Logic ---

  async function turnOffShutdownMode() {
    try {
      await axiosWithAuth().put(`/users/${user.user_id}/shutdown_mode`);
      setUser((prevUser) => {
        return { ...prevUser, shutdown_mode: false };
      });
    } catch (error) {
      console.error("Failed to turn off shutdown mode:", error);
    }
  }

  if (loading) {
    return (
      <Layout
        title="Loading..."
        rightElement={
          <button
            onClick={onClose}
            className="flex items-center bg-slate-200 py-2 px-3 rounded-lg text-slate-600"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {onCloseTitle ? onCloseTitle : "Back"}
          </button>
        }
      >
        <div className="flex justify-center items-center h-screen">
          <svg
            className="animate-spin h-16 w-16 text-darkBlue"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 0116 0H4z"
            ></path>
          </svg>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title={name ? name : user.user_id}
      rightElement={
        <button
          onClick={onClose}
          className="flex items-center bg-slate-200 py-2 px-3 rounded-lg text-slate-600"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          {onCloseTitle ? onCloseTitle : "Back"}
        </button>
      }
    >
      <div className="">
        {user.shutdown_mode && (
          <div className="p-6 mb-6 w-full rounded-lg bg-red-100 text-red-800 inline-block">
            <h2 className="text-2xl mb-4 border-b border-red-300 pb-2">
              Shutdown Mode Active
            </h2>
            <p>
              This user has been placed in temporary shutdown mode by the 1440
              system due to a high risk assessment. The user's access to the
              1440 platform and its features has been suspended until the
              situation can be evaluated. To regain access, the user must be
              cleared by a human coach who will review the circumstances and
              determine when it's safe for the user to resume using the
              platform.
            </p>
            <button
              onClick={turnOffShutdownMode}
              className="bg-red-200 hover:bg-red-300 text-red-800 font-medium py-2 px-4 rounded"
            >
              Turn off shutdown mode
            </button>
          </div>
        )}
        <div className="p-6 w-full rounded-lg bg-slate-100 text-slate-950 inline-block">
          <h2 className="text-2xl mb-6 border-b border-slate-300 pb-2">Chat</h2>
          <UserLogs
            conversation={conversation ? conversation : []}
            user_id={user.user_id}
            update_conversation={update_conversation}
          />
        </div>
        <div className="p-6 mt-6 w-full rounded-lg bg-slate-100 text-slate-950 inline-block">
          <h2 className="text-2xl mb-6 border-b border-slate-300 pb-2">
            Previous conversations
          </h2>
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3">
            {user?.summaries &&
              user.summaries.length > 0 &&
              user.summaries
                .slice()
                .reverse()
                .map((summary, index) => (
                  <SummaryCard
                    key={index}
                    summary={summary}
                    summary_id={index}
                    onSummaryOpen={markSummaryAsRead}
                  />
                ))}
          </div>
        </div>

        <div className="p-6 mt-6 w-full rounded-lg bg-slate-100 text-slate-950 inline-block">
          <h2 className="text-2xl mb-2 border-b border-slate-300 pb-2">
            Client Overview
          </h2>
          <div className="p-2 bg-slate-100 rounded text-slate-700">
            {headlineSummary}
          </div>
        </div>

        <div className="p-6 mt-6 w-full rounded-lg bg-slate-100 text-slate-950 inline-block">
          <h2 className="text-2xl mb-6 border-b border-slate-300 pb-2">
            Update
          </h2>
          <h3 className="text-lg font-medium mt-8 mb-0 text-slate-700">
            Give context to 1440
          </h3>
          <div className="mt-0 mb-0">
            <textarea
              id="humanCoachContext"
              value={humanCoachContext}
              onChange={(e) => handleHumanCoachContextChange(e.target.value)}
              className="border border-slate-300 rounded py-2 px-4 w-full mt-2"
              placeholder="Context about this user and information about how to approach future conversations..."
              rows={4}
            />
          </div>
        </div>

        {/* --- Active Module Section --- */}
        {user && user.protocols && user.protocols.length > 0 && (
          <div className="p-6 mt-6 w-full rounded-lg bg-slate-100 text-slate-950 inline-block">
            <ActiveModuleEditor
              activeModule={user.protocols[0]?.active_module}
              onUpdate={handleActiveModuleUpdate}
            />
            {user.protocols[0]?.active_module && (
              <button
                onClick={handleRemoveActiveModule}
                className="mt-4 bg-red-100 hover:bg-red-200 text-red-700 font-medium py-2 px-4 rounded"
              >
                Remove Active Module
              </button>
            )}
          </div>
        )}
        {/* --- End Active Module Section --- */}

        {/* --- Delete User Section --- */}
        <div className="p-6 mt-6 w-full rounded-lg bg-red-50 border border-red-200 text-slate-950 inline-block">
          <h3 className="text-lg font-medium text-red-700 mb-2">
            Delete User Account
          </h3>
          <p className="text-sm text-red-600 mb-4">
            Permanently deleting this user will remove all their associated data
            from the system. This action cannot be undone.
          </p>
          <button
            onClick={async () => {
              if (
                window.confirm(
                  "Are you sure you want to permanently delete this user and all their data? This action cannot be undone."
                )
              ) {
                try {
                  await axiosWithAuth().delete(
                    `/api/coach/users/${user.user_id}`
                  );
                  alert("User deleted successfully.");
                  onClose(); // Close the user detail view
                } catch (error) {
                  console.error("Failed to delete user:", error);
                  alert(
                    `Failed to delete user: ${
                      error.response?.data?.detail || error.message
                    }`
                  );
                }
              }
            }}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded"
          >
            Delete User Permanently
          </button>
        </div>
        {/* --- End Delete User Section --- */}

        <div className="mb-20"></div>
      </div>
    </Layout>
  );
};

export default User;
